/*
 * safeStorage.ts — defensive wrapper around localStorage.
 *
 * Why this exists (P1.6 from the 2026-05-23 codex audit): direct
 * `localStorage.getItem` / `setItem` calls can throw in:
 *   - Safari private mode (writes throw SecurityError / QuotaExceededError)
 *   - storage-quota exhaustion (legit user with a 5MB doc + plugins)
 *   - some embedded contexts where the storage API isn't exposed at all
 *
 * Any of those would crash the app on init (settings load) or interrupt
 * typing (autosave). The wrapper:
 *   - returns `null` / `false` on failure instead of throwing
 *   - falls back to an in-memory Map for failed writes so the SESSION
 *     still round-trips (the user sees their typing persist within the
 *     tab even if it won't survive a reload)
 *   - is the single chokepoint — anywhere in the app that reads/writes
 *     localStorage should go through these helpers
 *
 * Reads check the memory fallback FIRST so a session that's been writing
 * to fallback (because real storage is broken) reads back consistently.
 */

const memoryFallback = new Map<string, string>()

function hasStorage(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage !== null
  } catch {
    // Some environments throw on the bare `localStorage` access itself
    // (e.g. cross-origin iframe with disabled storage).
    return false
  }
}

export function safeGetItem(key: string): string | null {
  // Memory fallback wins — once we've written there for a session it's
  // the only source of truth (real storage is broken or stale).
  if (memoryFallback.has(key)) {
    return memoryFallback.get(key) ?? null
  }
  if (!hasStorage()) return null
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export function safeSetItem(key: string, value: string): boolean {
  if (!hasStorage()) {
    memoryFallback.set(key, value)
    return false
  }
  try {
    localStorage.setItem(key, value)
    // Real storage succeeded — drop any stale fallback entry so reads
    // come from the canonical source.
    memoryFallback.delete(key)
    return true
  } catch {
    // QuotaExceededError, SecurityError (Safari private), etc.
    // Stash in memory so the current session still round-trips.
    memoryFallback.set(key, value)
    return false
  }
}

export function safeRemoveItem(key: string): boolean {
  memoryFallback.delete(key)
  if (!hasStorage()) return false
  try {
    localStorage.removeItem(key)
    return true
  } catch {
    return false
  }
}

/**
 * Test-only: reset the in-memory fallback between cases. Not part of the
 * public app API; only invoked by safeStorage.test.ts.
 */
export function _resetMemoryFallback(): void {
  memoryFallback.clear()
}
